import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import SearchBar from '../../components/ui/SearchBar';
import { TableSkeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/PageLayout';
import { buildRepositoriesConnectedColumns } from '../../pages/Repositories/repositoriesTableColumns';
import { repositoryNameAndOwner } from './repositoriesPageUtils';
import { listRowClass } from '../../components/repository/repoViewTheme';
import { table, inputCompact, cn } from '../../components/ui/uiTheme';

const CONNECTED_REPO_COLUMN_ID = 'fullName';

function renderConnectedSortHeader(header) {
  const repoCol = header.column.id === CONNECTED_REPO_COLUMN_ID;
  return (
    <div
      className={`flex items-center group ${
        repoCol ? 'justify-between' : 'justify-center gap-1.5'
      } ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}
      onClick={header.column.getToggleSortingHandler()}
      onKeyDown={
        header.column.getCanSort()
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                header.column.getToggleSortingHandler()?.(e);
              }
            }
          : undefined
      }
      role={header.column.getCanSort() ? 'button' : undefined}
      tabIndex={header.column.getCanSort() ? 0 : undefined}
      aria-sort={
        header.column.getIsSorted() === 'asc'
          ? 'ascending'
          : header.column.getIsSorted() === 'desc'
            ? 'descending'
            : header.column.getCanSort()
              ? 'none'
              : undefined
      }
    >
      <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
      {header.column.getCanSort() ? (
        <span
          className={`opacity-60 group-hover:opacity-100 transition-opacity ${repoCol ? 'ml-2' : ''}`}
          aria-hidden
        >
          {header.column.getIsSorted() === 'asc' && <ArrowUp className="w-4 h-4 text-vivid-red" />}
          {header.column.getIsSorted() === 'desc' && (
            <ArrowDown className="w-4 h-4 text-vivid-red" />
          )}
          {!header.column.getIsSorted() && <ArrowUpDown className="w-4 h-4 text-slate-500" />}
        </span>
      ) : null}
    </div>
  );
}

function RepositoriesTablePagination({ tableInst, pageInput, setPageInput, idPrefix }: any) {
  if (tableInst.getPageCount() <= 1) return null;

  return (
    <div className="bg-dark-surface px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-dark-border sm:px-6">
      <p className="text-sm text-slate-300">
        Showing{' '}
        <span className="font-medium">
          {tableInst.getState().pagination.pageIndex * tableInst.getState().pagination.pageSize + 1}
        </span>{' '}
        to{' '}
        <span className="font-medium">
          {Math.min(
            (tableInst.getState().pagination.pageIndex + 1) *
              tableInst.getState().pagination.pageSize,
            tableInst.getPrePaginationRowModel().rows.length
          )}
        </span>{' '}
        of <span className="font-medium">{tableInst.getPrePaginationRowModel().rows.length}</span>
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-300">Page</span>
          <input
            type="number"
            min="1"
            max={tableInst.getPageCount()}
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const page = (e.target as HTMLInputElement).value
                  ? Number((e.target as HTMLInputElement).value) - 1
                  : 0;
                if (page >= 0 && page < tableInst.getPageCount()) {
                  tableInst.setPageIndex(page);
                  setPageInput('');
                }
              }
            }}
            placeholder={`${tableInst.getState().pagination.pageIndex + 1}`}
            className={cn(inputCompact, 'w-16 text-center py-1')}
            aria-label="Go to page"
            id={`${idPrefix}-page-input`}
          />
          <span className="text-sm text-slate-500">of {tableInst.getPageCount()}</span>
        </div>
        <nav className="inline-flex rounded-md -space-x-px" aria-label="Pagination">
          <button
            type="button"
            onClick={() => tableInst.previousPage()}
            disabled={!tableInst.getCanPreviousPage()}
            className="btn-secondary-mdl-sm relative inline-flex items-center rounded-l-md disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => tableInst.nextPage()}
            disabled={!tableInst.getCanNextPage()}
            className="btn-secondary-mdl-sm relative inline-flex items-center rounded-r-md disabled:opacity-50"
          >
            Next
          </button>
        </nav>
      </div>
    </div>
  );
}

/**
 * Connected repositories table (same UI as /repositories Connected tab).
 */
export default function RepositoriesConnectedTable({
  repositories = [],
  loading = false,
  navigate: navigateProp,
  showSearch = false,
  search = '',
  onSearchChange,
  searchPlaceholder = 'Search Repositories',
  emptyIcon,
  emptyTitle = 'No repositories connected yet.',
  emptyDescription,
  emptyAction,
  paginationIdPrefix = 'repos-connected',
  pageSize = 15,
}: any) {
  const navigateFromHook = useNavigate();
  const navigate = navigateProp || navigateFromHook;
  const [sorting, setSorting] = useState([{ id: CONNECTED_REPO_COLUMN_ID, desc: false }]);
  const [pageInput, setPageInput] = useState('');

  const columns = useMemo(() => buildRepositoriesConnectedColumns(navigate), [navigate]);

  const tableInst = useReactTable({
    data: repositories,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
  });

  useEffect(() => {
    tableInst.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repositories.length, search]);

  const goToRepo = (id) => {
    navigate(`/repositories/${id}`);
  };

  const handleConnectedRowKey = (repoId, e) => {
    if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
    if (e.key === ' ') e.preventDefault();
    goToRepo(repoId);
  };

  if (loading && repositories.length === 0) {
    return <TableSkeleton cols={5} rows={6} />;
  }

  return (
    <>
      {showSearch && onSearchChange ? (
        <div className="mb-4 flex justify-end">
          <div className="w-full max-w-md sm:w-64 sm:max-w-[50vw]">
            <SearchBar
              variant="compact"
              value={search}
              onChange={onSearchChange}
              placeholder={searchPlaceholder}
            />
          </div>
        </div>
      ) : null}

      {repositories.length === 0 ? (
        <EmptyState
          icon={emptyIcon}
          title={emptyTitle}
          description={emptyDescription}
          action={emptyAction}
        />
      ) : (
        <div className={cn(table.wrapper, 'overflow-hidden')}>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className={table.thead}>
                {tableInst.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        scope="col"
                        className={cn(
                          table.th,
                          header.column.id === CONNECTED_REPO_COLUMN_ID
                            ? 'text-left'
                            : 'text-center'
                        )}
                      >
                        {renderConnectedSortHeader(header)}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-dark-surface">
                {tableInst.getRowModel().rows.map((row) => {
                  const repo = row.original;
                  const label = repositoryNameAndOwner(repo).fullDisplay || 'Repository';
                  return (
                    <tr
                      key={row.id}
                      tabIndex={0}
                      aria-label={`Open repository ${label}`}
                      className={cn(
                        listRowClass,
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/40'
                      )}
                      onClick={() => goToRepo(repo.id)}
                      onKeyDown={(e) => handleConnectedRowKey(repo.id, e)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={cn(
                            table.td,
                            'max-w-[18rem] sm:max-w-none sm:whitespace-nowrap align-middle',
                            cell.column.id === CONNECTED_REPO_COLUMN_ID
                              ? 'text-left'
                              : 'text-center'
                          )}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <RepositoriesTablePagination
            tableInst={tableInst}
            pageInput={pageInput}
            setPageInput={setPageInput}
            idPrefix={paginationIdPrefix}
          />
        </div>
      )}
    </>
  );
}
